import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MAX_SCENES,
  type BroadcastScene,
  type SceneTransition,
  type SceneTransitionKind,
} from "../../lib/broadcastApi";

export const TRANSITION_KINDS: SceneTransitionKind[] = [
  "cut",
  "fade",
  "slide-left",
  "slide-right",
  "slide-up",
  "zoom",
];

interface Props {
  scenes: BroadcastScene[];
  programId: string;
  previewId: string;
  transition: SceneTransition;
  busy?: boolean;
  onPreview: (sceneId: string) => void;
  onTake: (sceneId: string, transition: SceneTransition) => void;
  onAspect: (sceneId: string, aspect: "16:9" | "16:10") => void;
  onCreate: () => void;
  onDuplicate: (sceneId: string) => void;
  onDelete: (sceneId: string) => void;
  onRename: (sceneId: string, name: string) => void;
  onMove: (sceneId: string, delta: -1 | 1) => void;
  onTransition: (transition: SceneTransition) => void;
}

/** Scene list: Preview / Program badges, Take, CRUD, order, default transition. */
export default function ScenesPanel(props: Props) {
  const { t } = useTranslation("broadcast");
  const {
    scenes,
    programId,
    previewId,
    transition,
    busy = false,
    onPreview,
    onTake,
    onAspect,
    onCreate,
    onDuplicate,
    onDelete,
    onRename,
    onMove,
    onTransition,
  } = props;
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(
    null,
  );
  // One-off transition for the next Take; starts from the session default.
  const [takeTr, setTakeTr] = useState<SceneTransition>(transition);
  useEffect(
    () => setTakeTr(transition),
    [transition.type, transition.duration_ms],
  );
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const full = scenes.length >= MAX_SCENES;
  const btn =
    "rounded-md border border-slate-600 px-2 py-1 text-xs font-medium text-slate-100 hover:bg-slate-700 disabled:opacity-40";

  return (
    <div
      data-testid="scenes-panel"
      className="rounded-lg border border-slate-700 p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          {t("control.scenes.title")}{" "}
          <span
            data-testid="scenes-count"
            className="text-xs font-normal text-slate-300"
          >
            {scenes.length}/{MAX_SCENES}
          </span>
        </h2>
        <button
          type="button"
          className={btn}
          disabled={busy || full}
          data-testid="scene-add"
          onClick={onCreate}
        >
          {t("control.scenes.add")}
        </button>
      </div>
      {full && (
        <p data-testid="scenes-full" className="text-xs text-amber-300">
          {t("control.scenes.full", { max: MAX_SCENES })}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label htmlFor="scene-transition-type">
          {t("control.scenes.transition")}
        </label>
        <select
          id="scene-transition-type"
          data-testid="scene-transition-type"
          className="rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-sm"
          value={takeTr.type}
          onChange={(e) =>
            setTakeTr({
              ...takeTr,
              type: e.target.value as SceneTransitionKind,
            })
          }
        >
          {TRANSITION_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`control.scenes.transitions.${k}`)}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={200}
          max={2000}
          step={50}
          aria-label={t("control.scenes.duration")}
          data-testid="scene-transition-duration"
          disabled={takeTr.type === "cut"}
          className="w-24 rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-sm disabled:opacity-40"
          value={takeTr.duration_ms}
          onChange={(e) =>
            setTakeTr({
              ...takeTr,
              duration_ms: Math.min(
                2000,
                Math.max(200, Number(e.target.value) || 500),
              ),
            })
          }
        />
        <span className="text-xs text-slate-300">ms</span>
        <button
          type="button"
          className={btn}
          disabled={busy}
          data-testid="scene-transition-default"
          onClick={() => onTransition(takeTr)}
        >
          {t("control.scenes.setDefault")}
        </button>
      </div>

      <ul className="space-y-2">
        {scenes.map((s, i) => {
          const isProgram = s.scene_id === programId;
          const isPreview = s.scene_id === previewId;
          return (
            <li
              key={s.scene_id}
              data-testid={`scene-row-${s.scene_id}`}
              data-program={isProgram ? "true" : "false"}
              data-preview={isPreview ? "true" : "false"}
              className={`rounded-md border p-2 ${
                isProgram
                  ? "border-red-500"
                  : isPreview
                    ? "border-emerald-500"
                    : "border-slate-700"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                {renaming?.id === s.scene_id ? (
                  <input
                    autoFocus
                    aria-label={t("control.scenes.rename")}
                    data-testid={`scene-name-input-${s.scene_id}`}
                    className="rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-sm"
                    value={renaming.name}
                    maxLength={60}
                    onChange={(e) =>
                      setRenaming({ id: s.scene_id, name: e.target.value })
                    }
                    onBlur={() => setRenaming(null)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && renaming.name.trim()) {
                        onRename(s.scene_id, renaming.name.trim());
                        setRenaming(null);
                      } else if (e.key === "Escape") {
                        setRenaming(null);
                      }
                    }}
                  />
                ) : (
                  <span
                    className="font-semibold"
                    data-testid={`scene-name-${s.scene_id}`}
                  >
                    {s.name}
                  </span>
                )}
                <select
                  aria-label={t("control.aspect")}
                  data-testid={`scene-aspect-${s.scene_id}`}
                  className="rounded border border-slate-600 bg-slate-800 px-1 py-0.5 text-xs"
                  value={s.aspect}
                  disabled={busy}
                  onChange={(e) =>
                    onAspect(s.scene_id, e.target.value as "16:9" | "16:10")
                  }
                >
                  <option value="16:9">16:9</option>
                  <option value="16:10">16:10</option>
                </select>
                {isProgram && (
                  <span
                    data-testid={`scene-badge-program-${s.scene_id}`}
                    className="rounded bg-red-600 px-1.5 text-xs font-bold text-white"
                  >
                    {t("control.scenes.program")}
                  </span>
                )}
                {isPreview && (
                  <span
                    data-testid={`scene-badge-preview-${s.scene_id}`}
                    className="rounded bg-emerald-700 px-1.5 text-xs font-bold text-white"
                  >
                    {t("control.scenes.preview")}
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                <button
                  type="button"
                  className={btn}
                  disabled={busy || isPreview}
                  data-testid={`scene-preview-${s.scene_id}`}
                  onClick={() => onPreview(s.scene_id)}
                >
                  {t("control.scenes.toPreview")}
                </button>
                <button
                  type="button"
                  className={`${btn} border-red-500 text-red-200`}
                  disabled={busy || isProgram}
                  data-testid={`scene-take-${s.scene_id}`}
                  onClick={() => onTake(s.scene_id, takeTr)}
                >
                  {t("control.scenes.take")}
                </button>
                <button
                  type="button"
                  className={btn}
                  disabled={busy}
                  data-testid={`scene-rename-${s.scene_id}`}
                  onClick={() => setRenaming({ id: s.scene_id, name: s.name })}
                >
                  {t("control.scenes.rename")}
                </button>
                <button
                  type="button"
                  className={btn}
                  disabled={busy || full}
                  data-testid={`scene-duplicate-${s.scene_id}`}
                  onClick={() => onDuplicate(s.scene_id)}
                >
                  {t("control.scenes.duplicate")}
                </button>
                <button
                  type="button"
                  className={btn}
                  disabled={busy || i === 0}
                  aria-label={t("control.scenes.moveUp")}
                  data-testid={`scene-up-${s.scene_id}`}
                  onClick={() => onMove(s.scene_id, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className={btn}
                  disabled={busy || i === scenes.length - 1}
                  aria-label={t("control.scenes.moveDown")}
                  data-testid={`scene-down-${s.scene_id}`}
                  onClick={() => onMove(s.scene_id, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className={`${btn} text-red-300`}
                  disabled={busy || isProgram || scenes.length <= 1}
                  title={isProgram ? t("control.scenes.deleteProgram") : ""}
                  data-testid={`scene-delete-${s.scene_id}`}
                  onClick={() => setConfirmDelete(s.scene_id)}
                >
                  {t("control.scenes.delete")}
                </button>
              </div>
              {confirmDelete === s.scene_id && (
                <div
                  role="alertdialog"
                  data-testid={`scene-delete-confirm-${s.scene_id}`}
                  className="mt-2 flex items-center gap-2 rounded border border-red-500 bg-red-950/40 p-2 text-xs"
                >
                  <span>
                    {t("control.scenes.confirmDelete", { name: s.name })}
                  </span>
                  <button
                    type="button"
                    className={btn}
                    data-testid={`scene-delete-yes-${s.scene_id}`}
                    onClick={() => {
                      setConfirmDelete(null);
                      onDelete(s.scene_id);
                    }}
                  >
                    {t("control.scenes.confirm")}
                  </button>
                  <button
                    type="button"
                    className={btn}
                    onClick={() => setConfirmDelete(null)}
                  >
                    {t("control.scenes.cancel")}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
